import Vue from 'vue'
import VueRouter from 'vue-router'
import store from './store/store'

Vue.use(VueRouter)

const Router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    { // login
      path: '/login',
      name: 'login',
      beforeEnter: (to, from, next) => {
        window.location.href = `${process.env.VUE_APP_API}/auth/login?redirect=${window.location.protocol}//${window.location.host}${to.query.redirect}`
      }
    },
    { // logout
      path: '/logout',
      name: 'logout',
      beforeEnter: (to, from, next) => {
        window.location.href = `${process.env.VUE_APP_API}/auth/logout`
      }
    },
    { // admin
      path: '/admin',
      name: 'admin',
      component: () => import('@/views/Admin.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: ':collection',
          name: 'adminCollection',
          component: () => import('@/components/admin/Collection.vue'),
          props: (route) => {
            const page = parseInt(route.query.page, 10)

            if (isNaN(page)) { return false }
            return { page }
          }
        },
        { path: ':collection/new', name: 'adminCreate', component: () => import('@/components/admin/collections/New.vue') },
        { path: ':collection/delete', name: 'adminDelete', component: () => import('@/components/admin/Collection.vue') },
        { path: ':collection/:id', name: 'adminUpdate', component: () => import('@/components/admin/collections/Edit.vue') }
      ]
    },
    { // text
      path: '/text/:id?',
      name: 'text',
      component: () => import('@/views/Text.vue'),
      props (to, from) {
        const id = to.params.id

        if (id && id !== '') {
          return {
            params: {
              id: id.split(',')
            },
            query: to.query
          }
        }
      }
    },
    {
      path: '/monitor',
      name: 'monitor',
      component: () => import('@/views/Monitor.vue')
    },
    { // directions
      path: '/directions/:id?',
      name: 'directions',
      component: () => import('@/views/Home.vue'),
      props (to, from) {
        const id = to.params.id

        if (id && id !== '') {
          return {
            params: {
              id: id.split(',')
            },
            query: to.query
          }
        }
      }
    },
    { // transit
      path: '/transit/:stopId',
      name: 'transit',
      component: () => import('@/views/Home.vue')
    },
    { // tour
      path: '/tour/:id/:step?',
      name: 'tour',
      component: () => import('@/views/Home.vue')
    },
    { // location
      path: '/location/:id?/:mode?',
      name: 'location',
      component: () => import('@/views/Home.vue'),
      beforeEnter: (to, from, next) => {
        // If there is more than one ID set and a mode
        if (to.params.id.split(',').length > 1 && to.params.mode) {
          return next({
            name: 'location',
            params: {
              id: to.params.id,
              mode: undefined
            },
            replace: true
          })
        }

        return next()
      },
      props (to, from) {
        const id = to.params.id

        if (id) {
          return {
            id: id.split(','),
            mode: to.params.mode
          }
        }
      }
    },
    { // home
      path: '/',
      name: 'home',
      alias: '/index.html',
      component: () => import('@/views/Home.vue')
    }
  ]
})

Router.beforeEach(async (to, from, next) => {
  if (process.env.RUN_ENV === 'local') {
    store.commit('setUser', {})
    return next()
  }

  // Return to the main page if the route has a location name with no associated location
  if (to.name === 'location' && !to.params.id) {
    return next({
      name: 'home',
      replace: true
    })
  }

  if (to.query.user) {
    store.commit('setUser', JSON.parse(to.query.user))

    const query = Object.assign({}, to.query)
    delete query.user

    return next({
      name: to.name,
      query: query,
      replace: true
    })
  }

  if (to.matched.some(record => record.meta.requiresAuth)) {
    // Load the user each time to verify they're still logged in
    try {
      await store.dispatch('loadUser', to.fullPath)
    } catch (e) {
      console.error(e)
      store.dispatch('login', to.fullPath)
    }

    const isAuthenticated = store.getters.isAuthenticated

    if (!isAuthenticated) {
      store.dispatch('login', to.fullPath)
    } else if (!store.getters.hasWritePermission) {
      next({
        name: 'home'
      })
    }
  }

  return next()
})

export default Router
